import React, { useState, useRef } from "react";
import { Box, Button, Select, MenuItem } from "@mui/material";
import { useSlate } from "slate-react";
import { Transforms, Editor, Range } from "slate";

const toggleMark = (editor, format, value = true) => {
  if (value === null) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, value);
  }
};

const EditorToolbar = () => {
  const editor = useSlate();
  const [horizontalPadding, setHorizontalPadding] = useState("");
  const [imageMode, setImageMode] = useState("image");
  const fileInputRef = useRef(null);

  const handleFontSizeChange = (event) => {
    const size = event.target.value;
    if (!editor.selection || Range.isCollapsed(editor.selection)) return;
    Editor.addMark(editor, "fontSize", size);
  };

  const handleColorChange = (event) => {
    const color = event.target.value;
    if (!editor.selection || Range.isCollapsed(editor.selection)) return;
    Editor.addMark(editor, "color", color);
  };

  const setAlignment = (editor, align) => {
    if (!editor.selection) return;

    Transforms.setNodes(
      editor,
      { align },
      {
        match: (n) => !Editor.isEditor(n) && n.type === "paragraph",
      },
    );
  };

  const setBlockStyle = (editor, style) => {
    if (!editor.selection) return;

    Transforms.setNodes(
      editor,
      {
        style,
      },
      {
        match: (n) => !Editor.isEditor(n) && n.type === "paragraph",
      },
    );
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);

    if (imageMode === "mediaText") {
      insertMediaText(editor, imageUrl, "image-left");
    } else {
      const imageNode = {
        type: "image",
        url: imageUrl,
        align: "center",
        width: "100%",
        children: [{ text: "" }],
      };

      Transforms.insertNodes(editor, imageNode);
    }

    event.target.value = "";
  };

  const insertImage = (editor, url) => {
    const imageNode = {
      type: "image",
      url,
      align: "center",
      width: "100%",
      children: [{ text: "" }],
    };

    Transforms.insertNodes(editor, imageNode);
  };

  const insertMediaText = (editor, imageUrl, layout = "image-left") => {
    const mediaNode = {
      type: "mediaText",
      layout,
      image: {
        url: imageUrl,
        width: "40%",
      },
      children: [
        {
          type: "paragraph",
          children: [{ text: "" }],
        },
      ],
    };

    const paragraph = {
      type: "paragraph",
      children: [{ text: "" }],
    };

    Transforms.insertNodes(editor, [mediaNode, paragraph]);
  };

  const updateLayout = (layout) => {
    Transforms.setNodes(
      editor,
      { layout },
      {
        match: (n) => !Editor.isEditor(n) && n.type === "mediaText",
      },
    );
  };

  const getActiveMediaTextEntry = () => {
    if (!editor.selection) return null;

    const [entry] = Editor.nodes(editor, {
      match: (n) => !Editor.isEditor(n) && n.type === "mediaText",
    });

    return entry || null;
  };
  const activeMedia = getActiveMediaTextEntry();


  return (
    <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
      {/* FONT SIZE */}
      <Select size="small" defaultValue="14px" onChange={handleFontSizeChange}>
        <MenuItem value="11px">11</MenuItem>
        <MenuItem value="12px">12</MenuItem>
        <MenuItem value="14px">14</MenuItem>
        <MenuItem value="16px">16</MenuItem>
        <MenuItem value="18px">18</MenuItem>
        <MenuItem value="24px">24</MenuItem>
      </Select>

      {/* COLOR PICKER */}
      <input
        type="color"
        onChange={handleColorChange}
        style={{
          width: "36px",
          height: "36px",
          border: "none",
          background: "none",
          cursor: "pointer",
        }}
      />

      {/* TEXT STYLES */}
      <Button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleMark(editor, "bold");
        }}
      >
        Bold
      </Button>

      <Button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleMark(editor, "italic");
        }}
      >
        Italic
      </Button>

      <Button
        onMouseDown={(e) => {
          e.preventDefault();
          toggleMark(editor, "underline");
        }}
      >
        Underline
      </Button>

      <Button
        onMouseDown={(e) => {
          e.preventDefault();
          setAlignment(editor, "left");
        }}
      >
        Left
      </Button>

      <Button
        onMouseDown={(e) => {
          e.preventDefault();
          setAlignment(editor, "center");
        }}
      >
        Center
      </Button>

      <Button
        onMouseDown={(e) => {
          e.preventDefault();
          setAlignment(editor, "right");
        }}
      >
        Right
      </Button>

      <input
        type="number"
        placeholder="Padding Horizontal"
        value={horizontalPadding}
        onChange={(e) => {
          const value = e.target.value;
          setHorizontalPadding(value);

          setBlockStyle(editor, {
            paddingLeft: `${value}px`,
            paddingRight: `${value}px`,
          });
        }}
        onBlur={() => setHorizontalPadding("")}
      />

      {/* <Button
        onMouseDown={(e) => {
          e.preventDefault();
          const url = window.prompt("Enter image URL");
          if (!url) return;
          insertImage(editor, url);
        }}
      >
        Image
      </Button> */}

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleImageUpload}
      />

      <Button
        onMouseDown={(e) => {
          e.preventDefault();
          fileInputRef.current.click();
        }}
      >
        Image
      </Button>

      <Button
        onMouseDown={(e) => {
          e.preventDefault();
          setImageMode("mediaText");
          fileInputRef.current.click();
        }}
      >
        Image + Text
      </Button>

      <Button onClick={() => updateLayout("image-left")}>Img Left</Button>
      <Button onClick={() => updateLayout("image-right")}>Img Right</Button>
      {activeMedia && (
        <input
          type="range"
          min="20"
          max="80"
          step="5"
          value={parseInt(activeMedia[0].image.width)}
          onChange={(e) => {
            const newWidth = `${e.target.value}%`;

            Transforms.setNodes(
              editor,
              {
                image: {
                  ...activeMedia[0].image,
                  width: newWidth,
                },
              },
              { at: activeMedia[1] },
            );
          }}
        />
      )}
    </Box>
  );
};

export default EditorToolbar;
